/**
 * Computes shader cutoff thresholds from the actual min/max range of a
 * raster image (NDVI or NDMI).
 *
 * The backend encodes the raw float values into an 8-bit PNG by normalising
 * the [min, max] range to [0, 1] pixel values. This means a pixel value of
 * 0 corresponds to `rawMin` and 1 corresponds to `rawMax`.
 *
 * The "zero crossing" — the pixel value that maps to a raw value of 0 —
 * separates non-vegetation (negative NDVI/NDMI) from vegetation.
 * We place the three cutoffs at the 50 %, 75 % and 100 % marks of the
 * positive (above-zero) pixel range.  This gives:
 *
 *   Zone 1 (veryLow): pixels below the 50 % mark  → noise + stressed
 *   Zone 2 (low):     50 %–75 % of positive range  → moderate
 *   Zone 3 (medium):  75 %–100 % of positive range → healthy
 *   Zone 4 (high):    above max pixel value         → peak only
 *
 * @param rawMin – minimum value reported by the backend (may be negative)
 * @param rawMax – maximum value reported by the backend
 */
export const computeCutoffsFromRange = (
  rawMin: number,
  rawMax: number
): { cutoffMin: number; mid: number; cutoffMax: number } => {
  const range = rawMax - rawMin;

  if (range <= 0) {
    // Degenerate case — fall back to even split
    return { cutoffMin: 0.25, mid: 0.5, cutoffMax: 0.75 };
  }

  // Where does the raw value 0 sit in normalised [0, 1] pixel space?
  const zeroCrossing = rawMin < 0 ? -rawMin / range : 0;

  // Width of the positive-value portion of the pixel range
  const positiveRange = 1 - zeroCrossing;

  return {
    cutoffMin: Number.parseFloat((zeroCrossing + 0.5 * positiveRange).toFixed(4)),
    mid: Number.parseFloat((zeroCrossing + 0.75 * positiveRange).toFixed(4)),
    cutoffMax: 1,
  };
};
